import { ChevronRight, AlertTriangle, Target } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Info } from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useNavigate } from 'react-router-dom';
import CustomTooltip from '../../components/tooltip/CustomTooltip';
import { ProgressAnalysisSkeleton } from './ProgressAnalysisSkeleton';

export default function ProgressAnalysis({ packId }) {
  const generateCardData = (overviewData) => {
    if (!overviewData) return [];

    function trimTrailingZeros(num) {
      return parseFloat(num?.toString());
    }
    const subjects = Object.keys(overviewData).filter((key) => key !== 'overall');
    const overall = overviewData?.overall;
    const accuracySubjects = subjects?.map((subjectId) => {
      const subject = overviewData[subjectId];
      return {
        name: subject?.subjectShortName,
        value: `${trimTrailingZeros(subject?.accuracy)}%`,
      };
    });

    const mistakenSubjects = subjects.map((subjectId) => {
      const subject = overviewData[subjectId];
      const mistaken = subject?.attempted - subject?.correct;
      return {
        name: subject?.subjectShortName,
        value: `${trimTrailingZeros(mistaken)}Qs`,
      };
    });

    const totalMistaken = overall?.attempted - overall?.correct;

    return [
      {
        title: 'Accuracy',
        icon: <Target size={24} style={{ color: 'var(--q3-blue-normal)' }} />,
        mainValue: `${trimTrailingZeros(overall?.accuracy)}%`,
        color: '--q3-blue-normal',
        tooltip: 'Your overall accuracy across subjects',
        subjects: accuracySubjects,
        footerText: 'View Details',
        url: `/v3/pack-analysis`,
      },
      {
        title: 'Mistaken Qs',
        icon: <AlertTriangle size={24} style={{ color: 'var(--q3-orange-normal)' }} />,
        mainValue: (
          <>
            {totalMistaken}
            <span className="text-2xl">Qs</span>
          </>
        ),
        color: '--q3-orange-normal',
        tooltip: 'Questions you answered incorrectly',
        subjects: mistakenSubjects,
        footerText: 'View All Mistaken Qs',
        url: `/v3/my-mistakes`,
      },
      {
        title: 'Quality of Attempts',
        icon: <Target size={24} style={{ color: 'var(--q3-neutral-default)' }} />,
        mainValue: null,
        color: '--q3-neutral-default',
        tooltip: 'Analysis of your answer quality',
        qualityMetrics: [
          {
            value: `${trimTrailingZeros(overall?.perfectAttempts)}%`,
            label: 'Perfect',
            className: ' text-[var(--q3-green-normal)] ',
          },
          {
            value: `${trimTrailingZeros(overall?.wastedAttempts)}%`,
            label: 'Wasted',
            className: ' text-[var(--q3-danger-normal)]',
          },
          {
            value: `${trimTrailingZeros(overall?.overtimeAttempts)}%`,
            label: 'Overtime',
            className: ' text-[var(--q3-yellow-normal)] ',
          },
          {
            value: `${trimTrailingZeros(overall?.confusedAttempts)}%`,
            label: 'Confused',
            className: ' text-[var(--q3-neutral-default)]  ',
          },
        ],
        footerText: 'View Details',
        url: `/v3/pack-analysis/quality-of-attempt`,
      },
    ];
  };
  const [packs, setPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState('');
  const [selectedTestCount, setSelectedTestCount] = useState('1');
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchPacks = async () => {
      try {
        const response = await axios.get(`/api/packs/${packId}/analysis/groups`);
        const packsData = response?.data?.data;
        setPacks(packsData);
        if (packsData?.length > 0) {
          setSelectedPack(packsData[0]._id);
        }
      } catch (error) {
        setPacks([]);
        console.error('Error fetching packs:', error);
      }
    };
    if (packId) {
      fetchPacks();
    }
  }, [packId]);

  useEffect(() => {
    if (selectedPack) {
      fetchOverviewData();
    }
  }, [selectedPack, selectedTestCount]);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/packs/v2/analysis/${packId}/overview?groupId=${selectedPack}&count=${selectedTestCount}`,
      );
      setOverviewData(response?.data?.data);
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestCountChange = (count) => {
    setSelectedTestCount(count);
  };

  const handlePackChange = (packId) => {
    setSelectedPack(packId);
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;

    if (!isScrollable) {
      setShowLeftShadow(false);
      setShowRightShadow(false);
      return;
    }

    setShowLeftShadow(scrollLeft > 0);
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
  };
  const cardData = generateCardData(overviewData).map((card) => {
    if (card.title === 'Accuracy' && ['3', '5'].includes(selectedTestCount)) {
      return {
        ...card,
        url: `/v3/pack-analysis/performance?test-count=${selectedTestCount}`,
      };
    } else if (card.title === 'Quality of Attempts' && ['3', '5'].includes(selectedTestCount)) {
      return {
        ...card,
        url: `/v3/pack-analysis/quality-of-attempt?test-count=${selectedTestCount}`,
      };
    } else if (card.title === 'Mistaken Qs' && ['3', '5'].includes(selectedTestCount)) {
      return {
        ...card,
        url: `/v3/my-mistakes?test-count=${selectedTestCount}`,
      };
    }
    return card;
  });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Check initial scroll state
      handleScroll();

      // Add scroll listener
      container.addEventListener('scroll', handleScroll);

      // Add resize listener to handle container size changes
      const resizeObserver = new ResizeObserver(handleScroll);
      resizeObserver.observe(container);

      return () => {
        container.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [cardData]);

  const handleViewDetailedAnalysis = () => {
    const filterParam = ['3', '5'].includes(selectedTestCount) ? `?test-count=${selectedTestCount}` : '';
    navigate(`/v3/pack-analysis/performance${filterParam}`);
  };

  if (!packs?.length > 0) {
    return <></>;
  } else
    return (
      <div className="border-t-1 border-b-1 py-3 px-4 md:px-2 md:border-none ">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-2xl font-bold text-[var(--q3-neutral-default)] mb-2">Quick Analysis</h1>
          <Button
            variant="outline"
            onClick={handleViewDetailedAnalysis}
            className="border-0 md:border !bg-[var(--q3-surface-default)]"
          >
            <div className="flex items-center">
              <span className="text-[var(--q3-accent-normal)] font-semibold text-sm hidden sm:inline">
                View Detailed Analysis
              </span>
              <span className="text-[var(--q3-accent-normal)] inline sm:hidden">View All</span>
              <ArrowRight className="hidden sm:inline  h-4 w-4 ml-1 text-[var(--q3-accent-normal)]" />
            </div>
          </Button>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto scroll-shadow-x whitespace-nowrap">
          <Select value={selectedPack} onValueChange={handlePackChange}>
            <SelectTrigger className="rounded-4xl border-1 border-[var(--q3-stroke-normal)] !bg-[var(--q3-surface-default)] py-0 text-[var(--q3-neutral-default)] cursor-pointer">
              <SelectValue placeholder={<div className="font-semibold">Select Test</div>} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {packs?.map((each) => {
                  const isSelected = selectedPack === each._id;
                  return (
                    <SelectItem
                      className={`px-5 py-2 font-semibold cursor-pointer w-full ${
                        isSelected
                          ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]'
                          : 'bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]'
                      }`}
                      key={each._id}
                      value={each?._id}
                    >
                      <span className="font-medium">{each?.title}</span>
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            </SelectContent>
          </Select>
          <Button
            variant={selectedTestCount === '1' ? 'default' : 'outline'}
            className={`rounded-full px-4 py-2 font-semibold transition-colors ${
              selectedTestCount === '1'
                ? 'bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)]/90 text-[var(--q3-neutral-default_inverted)]'
                : '!bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]'
            }`}
            onClick={() => handleTestCountChange('1')}
            disabled={loading}
          >
            Last Test
          </Button>

          <Button
            variant={selectedTestCount === '3' ? 'default' : 'outline'}
            className={`rounded-full px-4 py-2 font-semibold transition-colors ${
              selectedTestCount === '3'
                ? 'bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)]/90 text-[var(--q3-neutral-default_inverted)]'
                : '!bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]'
            }`}
            onClick={() => handleTestCountChange('3')}
            disabled={loading}
          >
            Last 3 Tests
          </Button>

          <Button
            variant={selectedTestCount === '5' ? 'default' : 'outline'}
            className={`rounded-full px-4 py-2 font-semibold transition-colors ${
              selectedTestCount === '5'
                ? 'bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)]/90 text-[var(--q3-neutral-default_inverted)]'
                : '!bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]'
            }`}
            onClick={() => handleTestCountChange('5')}
            disabled={loading}
          >
            Last 5 Tests
          </Button>
        </div>
        <div className="relative">
          {showLeftShadow && (
            <div className="absolute left-0 top-0 bottom-3 w-8 z-3 pointer-events-none [background:linear-gradient(to_right,var(--q3-neutral-default_inverted),transparent)]" />
          )}

          {showRightShadow && (
            <div className="absolute right-0 top-0 bottom-3 w-8 z-3 pointer-events-none [background:linear-gradient(to_left,var(--q3-neutral-default_inverted),transparent)]" />
          )}

          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto pb-4 h-full"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {loading ? (
              <ProgressAnalysisSkeleton />
            ) : cardData.length > 0 ? (
              cardData.map((card, index) => (
                <Card
                  key={index}
                  style={{
                    borderColor: `var(--q3-stroke-normal)`,
                  }}
                  onClick={() => navigate(card?.url)}
                  className="gap-5 cursor-pointer bg-[var(--q3-surface-default)] shadow-none rounded-xl border-1 flex-shrink-0 w-[331.5px] h-[150px] flex flex-col items-start justify-center p-2 py-4"
                >
                  <CardHeader className="flex flex-row items-center w-full justify-between px-3">
                    <CardTitle className="text-lg flex gap-2 items-center font-semibold">
                      <span className="text-[var(--q3-neutral-light)]">{card?.title}</span>
                      {/* <CustomTooltip text={card?.tooltip}>
                        <Info size={13} className="cursor-pointer text-[var(--q3-neutral-light)] " />
                      </CustomTooltip> */}
                    </CardTitle>
                    {/* <div>{card?.icon}</div> */}
                  </CardHeader>
                  <CardContent className="w-full px-3">
                    <div className="flex items-center w-full gap-5">
                      {card?.mainValue && (
                        <div className={`text-4xl font-bold text-var(--q3-neutral-default)`}>{card?.mainValue}</div>
                      )}

                      {card?.subjects ? (
                        <div
                          className={`flex flex-nowrap ${card?.subjects.length === 3 ? 'gap-8' : 'gap-4'} text-center`}
                        >
                          {card?.subjects.map((subject, i) => (
                            <div key={i} className="text-xs text-left text-[var(--q3-neutral-light)] ">
                              <div>{subject.name}</div>
                              <div className="font-bold">{subject.value}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-4 gap-2 w-full">
                          {card?.qualityMetrics?.map((metric, i) => (
                            <div key={i} className="w-full -mt-1 -mb-1 text-left">
                              <div className={`${metric.className} text-lg font-bold mb-0 w-full text-left`}>
                                {metric.value}
                              </div>
                              <div className="text-[var(--q3-neutral-light)] text-sm mt-0 text-left">
                                {metric.label}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>

                  <CardFooter className="pt-0 w-full px-0 cursor-pointer">
                    <Button onClick={() => navigate(card?.url)} variant="link" asChild className={`p-0 h-auto`}>
                      <div className="flex items-center text-sm text-[var(--q3-neutral-light)]">
                        <div className="text-[var(--q3-neutral-light)]">{card?.footerText}</div>
                        <ChevronRight
                          style={{ width: '12px', height: '12px' }}
                          className="mt-0.5 text-[var(--q3-neutral-light)]"
                        />
                      </div>
                    </Button>
                  </CardFooter>
                </Card>
              ))
            ) : selectedPack ? (
              <div className="flex items-center justify-center w-full h-32">
                <div className="text-lg text-gray-500">No data available</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
}
